import { Vec2d, Vec2dSimple, VecLike } from './Vec2d';
import { GeometryUtils } from './geometry';

/**
 * A serializable model for 2D vectors.
 *
 * @public */
export interface Point2dModel extends Vec2dSimple {
    /** 压力值 */
    z: number
    /** 基于上一点的向量 */
	v?: VecLike
    /** 时间戳 */
    t?: number
    /** 当前点基于前后两个点的夹角度数(0-360) */
	a?: number
}

/** @public */
export type PointLike = Point2d | Point2dModel

/** @public */
export class Point2d extends Vec2d {
  constructor(public x = 0, public y = 0, public z = 0, public v = { x:0, y:0 }, public t = 0, public a = 0) {
    super(x, y, z);
  }
  get timestamp(): number{
    return this.t;
  }
  get pressure() {
    return this.z;
  }
  get angleNum(){
    return this.a;
  }
  get XY():[number,number] {
    return [this.x, this.y];
  }
  setA(a:number) {
    this.a = a;
  }
  setT(t:number) {
    this.t = t;
  }
  setv(v: Vec2dSimple) {
    this.v = { x:v.x, y:v.y };
    return this;
  }
  set(x = this.x, y = this.y, z = this.z, v = this.v, t = this.t, a = this.a) {
    this.x = x;
    this.y = y;
    this.z = z;
    this.v = v;
    this.t = t;
    this.a = a;
    return this;
  }
  clone(): Point2d {
    const { x, y, z, v, t, a } = this;
    const _v = { x:v.x, y:v.y };
    return new Point2d(x, y, z, _v, t,a);
  }
  distance(p: Point2d): number {
    return GeometryUtils.distance(this, p);
  }
  isNear(p: Point2d, tolerance:number): boolean {
    return GeometryUtils.isNear(this, p, tolerance);
  }
  getAngleByPoints(pre:Point2d, next:Point2d){
    return GeometryUtils.angleBetweenPoints(pre, this, next);
  }
  static Sub(A: PointLike, B: VecLike): Point2d {
    return new Point2d(A.x - B.x, A.y - B.y);
  }
  static Add(A: PointLike, B: VecLike): Point2d {
    return new Point2d(A.x + B.x, A.y + B.y);
  }
  /**
   * 以C为旋转中心, 旋转A点, 旋转角度为r
   * 返回值为旋转后的点
   * @param A 要旋转的点
   * @param C 旋转中心点
   * @param r 旋转角度（弧度制）
   * @param decimal 精度小数位数
   * @returns 旋转后的点
   */
  static RotWith(A: Point2d | Vec2d, C: Point2d | Vec2d, r: number, decimal:number = 2 ): Point2d {
    const x = A.x - C.x;
    const y = A.y - C.y;
    const s = Math.sin(r);
    const c = Math.cos(r);
    const step = Math.pow(10, decimal);
    const X = Math.floor((C.x + (x * c - y * s)) * step) / step;
    const Y = Math.floor((C.y + (x * s + y * c)) * step) / step;
    return new Point2d(X, Y);
  }
  /**
     * 根据圆心和半径，获取圆上的等份点
     * @param o 圆心
     * @param radius 半径 
     * @param average 均分数
     * @returns 
     */
  static GetDotStroke(o:Point2d, radius:number, average:number = 16):Point2d[] {
    const circlePoints = GeometryUtils.getCirclePoints(o, radius, average);
    return circlePoints.map(p => new Point2d(p.x, p.y));
  }
  /**
     * 根据圆心和圆上的起始点，获取半圆上的等份点
     * @param o 圆心 
     * @param p 圆弧起始点
     * @param radian 1，顺时针 -1，逆时针 
     * @param average 均分数
     * @returns 
     */
  static GetSemicircleStroke(o:Point2d, p:Point2d, radian:number = -1, average:number = 8):Point2d[] {
    const endAngle = radian * Math.PI;
    const arcPoints = GeometryUtils.getArcPoints(o, p, endAngle, average);
    return arcPoints.map(point => new Point2d(point.x, point.y));
  }
}